import { useAppTheme } from "@hooks/useAppTheme";
import { Box, MarkdownRenderer } from "@memorang/ui";
import { useState } from "react";
import { Pressable } from "react-native";
import { Icon, Text } from "react-native-paper";

interface Step {
	title: string;
	description: string;
}

interface Props {
	steps: Step[];
}

const StepItem = ({
	step,
	expanded,
	reviewed,
	isLast,
	onPress,
}: {
	step: Step;
	expanded: boolean;
	reviewed: boolean;
	isLast: boolean;
	onPress: () => void;
}) => {
	const theme = useAppTheme();

	return (
		<Box
			borderBottomWidth={isLast && !expanded ? 0 : 1}
			borderBottomColor={theme.colors.outlineVariant}
		>
			<Pressable
				onPress={onPress}
				accessibilityRole="button"
				accessibilityState={{ expanded }}
				accessibilityLabel={step.title}
				style={{
					borderLeftWidth: 4,
					borderLeftColor: expanded ? theme.colors.primary : "transparent",
					backgroundColor: expanded
						? theme.colors.primaryContainer
						: "transparent",
				}}
			>
				<Box
					flexDirection="row"
					alignItems="center"
					gap={12}
					minHeight={56}
					paddingHorizontal={16}
					paddingVertical={14}
				>
					<Box flex={1}>
						<Text
							variant="titleSmall"
							style={{ color: theme.colors.onSurface, fontWeight: "700" }}
						>
							{step.title}
						</Text>
					</Box>
					{reviewed ? null : (
						<Box
							width={8}
							height={8}
							borderRadius={4}
							backgroundColor={theme.colors.error}
						/>
					)}
					<Icon
						source={expanded ? "chevron-up" : "chevron-down"}
						size={24}
						color={theme.colors.onSurfaceVariant}
					/>
				</Box>
			</Pressable>
			{expanded ? (
				<Box paddingHorizontal={16} paddingTop={12} paddingBottom={16}>
					<MarkdownRenderer
						text={step.description}
						fontSize={15}
						variant="explanation"
					/>
				</Box>
			) : null}
		</Box>
	);
};

const ScenarioStepsAccordion = ({ steps }: Props) => {
	const theme = useAppTheme();
	const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
	const [reviewed, setReviewed] = useState<Set<number>>(() => new Set());

	if (!steps.length) {
		return null;
	}

	const toggle = (index: number) => {
		setExpanded((current) => {
			const next = new Set(current);
			if (next.has(index)) {
				next.delete(index);
			} else {
				next.add(index);
			}
			return next;
		});
		setReviewed((current) => new Set(current).add(index));
	};

	return (
		<Box
			borderWidth={1}
			borderColor={theme.colors.outlineVariant}
			overflow="hidden"
			backgroundColor={theme.colors.surface}
		>
			{steps.map((step, index) => (
				<StepItem
					key={step.title}
					step={step}
					expanded={expanded.has(index)}
					reviewed={reviewed.has(index)}
					isLast={index === steps.length - 1}
					onPress={() => toggle(index)}
				/>
			))}
		</Box>
	);
};

export default ScenarioStepsAccordion;
